import React, { useEffect, useState, useMemo } from "react";
import { Loader2, Plus, BookOpen, Ban, Eye } from "lucide-react";
import { authFetch } from "../../lib/api";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import AdminLoading from "../../components/admin/AdminLoading";
import AdminEmptyState from "../../components/admin/AdminEmptyState";
import AdminTable, { AdminMobileCard, adminTableHeadClass, adminTableRowClass, adminClickableRowClass } from "../../components/admin/AdminTable";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import AdminDetailDialog, {
  AdminDetailField,
  AdminDetailSection,
} from "../../components/admin/AdminDetailDialog";
import {
  ADMIN_CHIP,
  ADMIN_CHIP_ACTIVE,
  ADMIN_FIELD,
  ADMIN_LABEL,
  ADMIN_PANEL,
  ADMIN_SELECT,
  BTN_OUTLINE,
  BTN_PRIMARY,
} from "../../config/brand";
import { toast } from "sonner";

const statusColors = {
  pending: "secondary",
  checkout: "secondary",
  confirmed: "default",
  cancelled: "destructive",
};

const statusLabels = {
  pending: "En attente",
  checkout: "Paiement en cours",
  confirmed: "Confirmée",
  cancelled: "Annulée",
};

const paymentModeLabels = {
  stripe: "Stripe",
  simulated: "Simulation",
  free: "Gratuit",
  offline: "Sur place",
  admin: "Saisie admin",
};

const FILTERS = [
  { value: "all", label: "Toutes" },
  { value: "unread", label: "Non lues" },
  { value: "leads", label: "Leads panier" },
  { value: "pending", label: "En attente" },
  { value: "checkout", label: "Paiement en cours" },
  { value: "confirmed", label: "Confirmées" },
  { value: "cancelled", label: "Annulées" },
];

const emptyForm = {
  schedule_id: "",
  parent_name: "",
  parent_email: "",
  parent_phone: "",
  child_name: "",
  child_age: "",
  amount: "",
  status: "confirmed",
};

function bookingTypeFromSchedule(selected) {
  const category = selected?.offer_category;
  if (category === "stage" || category === "cursus" || category === "atelier") {
    return category;
  }
  return "atelier";
}

function childLabel(booking) {
  if (!booking.child_name) return "—";
  return booking.child_age ? `${booking.child_name} (${booking.child_age} ans)` : booking.child_name;
}

function typeLabel(booking) {
  if (booking.waitlist_request) return "Liste d'attente";
  if (booking.appointment_request) return "RDV à rappeler";
  if (booking.is_lead) return "Lead panier";
  const labels = { atelier: "Atelier", cursus: "Cursus", stage: "Stage" };
  return labels[booking.booking_type] || booking.booking_type || "—";
}

function paymentLabel(booking) {
  if (booking.created_by_admin || booking.payment_intent_id === "admin") {
    return paymentModeLabels.admin;
  }
  if (booking.payment_mode && paymentModeLabels[booking.payment_mode]) {
    return paymentModeLabels[booking.payment_mode];
  }
  if (booking.payment_intent_id) return booking.payment_intent_id;
  return "—";
}

function money(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return `${n} €`;
}

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      const [bRes, sRes] = await Promise.all([
        authFetch("/api/admin/bookings"),
        authFetch("/api/admin/schedules"),
      ]);
      if (bRes.ok) setBookings(await bRes.json());
      else toast.error("Impossible de charger les réservations");
      if (sRes.ok) setSchedules(await sRes.json());
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return bookings;
    if (filter === "unread") return bookings.filter((b) => b.unread);
    if (filter === "leads") return bookings.filter((b) => b.is_lead);
    return bookings.filter((b) => b.status === filter);
  }, [bookings, filter]);

  const counts = useMemo(() => {
    const c = { all: bookings.length, unread: 0, leads: 0, pending: 0, checkout: 0, confirmed: 0, cancelled: 0 };
    bookings.forEach((b) => {
      if (b.unread) c.unread += 1;
      if (b.is_lead) c.leads += 1;
      if (c[b.status] !== undefined) c[b.status] += 1;
    });
    return c;
  }, [bookings]);

  const markRead = (booking) => {
    if (!booking?.unread) return;
    setBookings((prev) => prev.map((b) => (b.id === booking.id ? { ...b, unread: false } : b)));
    authFetch(`/api/admin/bookings/${booking.id}/read`, { method: "POST" }).catch(() => {});
  };

  const openDetail = (booking) => {
    setDetail(booking);
    markRead(booking);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const scheduleLabel = (s) => {
    const product = s.formula_name || s.offer_name || s.workshop_name || "—";
    const cat = s.offer_category ? ` [${s.offer_category}]` : "";
    return `${s.center_name} · ${product}${cat} · ${s.day_of_week} ${s.start_time}`;
  };

  const validate = () => {
    const next = {};
    if (!form.schedule_id) next.schedule_id = "Sélectionnez un créneau";
    if (!form.parent_name.trim()) next.parent_name = "Le nom du parent est requis";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleCreate = async () => {
    if (!validate()) return;

    setSaving(true);
    const selected = schedules.find((s) => s.id === form.schedule_id);
    try {
      const res = await authFetch("/api/admin/bookings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          schedule_id: form.schedule_id,
          booking_type: bookingTypeFromSchedule(selected),
          offer_id: selected?.offer_id || null,
          formula_id: selected?.formula_id || null,
          parent_name: form.parent_name,
          parent_email: form.parent_email,
          parent_phone: form.parent_phone,
          child_name: form.child_name,
          child_age: form.child_age ? Number(form.child_age) : null,
          amount: Number(form.amount) || 0,
          status: form.status,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Erreur");
      }
      toast.success("Réservation créée");
      setDialog(false);
      setForm(emptyForm);
      setErrors({});
      setLoading(true);
      load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const openDialog = () => {
    setForm(emptyForm);
    setErrors({});
    setDialog(true);
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const res = await authFetch(`/api/admin/bookings/${cancelTarget.id}/cancel`, { method: "POST" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Erreur");
      }
      toast.success(`Réservation de ${cancelTarget.parent_name} annulée`);
      setCancelTarget(null);
      load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCancelling(false);
    }
  };

  const viewButton = (booking) => (
    <Button
      variant="ghost"
      size="sm"
      className="text-[#3a00e0] hover:text-[#4400e8]"
      onClick={(e) => {
        e.stopPropagation();
        openDetail(booking);
      }}
      aria-label={`Voir la réservation de ${booking.parent_name}`}
    >
      <Eye className="h-4 w-4 mr-1" />
      Voir
    </Button>
  );

  const cancelButton = (booking) =>
    booking.status !== "cancelled" ? (
      <Button
        variant="ghost"
        size="sm"
        className="text-amber-600 hover:text-amber-700"
        onClick={(e) => {
          e.stopPropagation();
          setCancelTarget(booking);
        }}
        aria-label={`Annuler la réservation de ${booking.parent_name}`}
      >
        <Ban className="h-4 w-4 mr-1" />
        Annuler
      </Button>
    ) : null;

  const rowActions = (booking) => (
    <div className="flex items-center justify-end gap-1">
      {viewButton(booking)}
      {cancelButton(booking)}
    </div>
  );

  return (
    <div>
      <AdminPageHeader
        icon={BookOpen}
        title="Réservations"
        description={`${bookings.length} réservation(s) au total`}
        action={
          <Button onClick={openDialog} className={BTN_PRIMARY}>
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle réservation
          </Button>
        }
      />

      <div className="flex gap-2 mb-6 flex-wrap" role="group" aria-label="Filtrer les réservations">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setFilter(f.value)}
            aria-pressed={filter === f.value}
            className={filter === f.value ? ADMIN_CHIP_ACTIVE : ADMIN_CHIP}
          >
            {f.label} ({counts[f.value] ?? 0})
          </button>
        ))}
      </div>

      {loading ? (
        <AdminLoading message="Chargement des réservations..." />
      ) : filtered.length === 0 ? (
        <AdminEmptyState
          icon={BookOpen}
          showMascot={filter === "all"}
          title={filter === "all" ? "Aucune réservation" : "Aucun résultat"}
          description={
            filter === "all"
              ? "Les réservations apparaîtront ici dès qu'un client réserve."
              : "Aucune réservation ne correspond à ce filtre."
          }
          action={
            filter === "all" ? (
              <Button onClick={openDialog} className={BTN_PRIMARY}>
                <Plus className="h-4 w-4 mr-2" />
                Nouvelle réservation
              </Button>
            ) : null
          }
        />
      ) : (
        <AdminTable
          mobileCards={filtered.map((booking) => (
            <AdminMobileCard
              key={booking.id}
              title={booking.parent_name}
              subtitle={booking.parent_email || undefined}
              onClick={() => openDetail(booking)}
              badge={
                <Badge variant={statusColors[booking.status] || "secondary"}>
                  {statusLabels[booking.status] || booking.status}
                </Badge>
              }
              meta={[
                { label: "Enfant", value: childLabel(booking) },
                { label: "Offre", value: booking.schedule_info?.workshop_name },
                { label: "Montant", value: money(booking.amount) },
                { label: "Type", value: typeLabel(booking) },
              ]}
              actions={rowActions(booking)}
            />
          ))}
        >
          <Table>
            <TableHeader>
              <TableRow className={`${adminTableRowClass} hover:bg-transparent`}>
                <TableHead className={adminTableHeadClass}>Parent</TableHead>
                <TableHead className={adminTableHeadClass}>Enfant</TableHead>
                <TableHead className={adminTableHeadClass}>Offre / Atelier</TableHead>
                <TableHead className={adminTableHeadClass}>Centre</TableHead>
                <TableHead className={adminTableHeadClass}>Créneau</TableHead>
                <TableHead className={adminTableHeadClass}>Montant</TableHead>
                <TableHead className={adminTableHeadClass}>Statut</TableHead>
                <TableHead className={adminTableHeadClass}>Type</TableHead>
                <TableHead className={adminTableHeadClass}>Date</TableHead>
                <TableHead className={`${adminTableHeadClass} text-right`}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((booking) => (
                <TableRow
                  key={booking.id}
                  className={adminClickableRowClass}
                  onClick={() => openDetail(booking)}
                >
                  <TableCell className="text-slate-900">
                    <div className="flex items-center gap-2">
                      {booking.unread ? (
                        <span className="w-1.5 h-1.5 rounded-full bg-[#3a00e0] shrink-0" aria-label="Non lu" />
                      ) : null}
                      <span>{booking.parent_name}</span>
                    </div>
                    <div className="text-xs text-slate-500">{booking.parent_email}</div>
                    {booking.parent_phone ? (
                      <div className="text-xs text-slate-400">{booking.parent_phone}</div>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-slate-700">{childLabel(booking)}</TableCell>
                  <TableCell className="text-slate-700">
                    {booking.schedule_info?.workshop_name || "—"}
                  </TableCell>
                  <TableCell className="text-slate-700">
                    {booking.schedule_info?.center_name || "—"}
                  </TableCell>
                  <TableCell className="text-slate-700">
                    {booking.schedule_info
                      ? `${booking.schedule_info.day} ${booking.schedule_info.time}`
                      : "—"}
                  </TableCell>
                  <TableCell className="text-slate-700 tabular-nums">
                    {booking.amount} €
                    {booking.discount > 0 && (
                      <span className="text-xs text-emerald-600 block">
                        -{booking.discount} € ({booking.promo_code})
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusColors[booking.status] || "secondary"}>
                      {statusLabels[booking.status] || booking.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {booking.waitlist_request ? (
                      <Badge variant="outline" className="border-orange-300 text-orange-700">
                        Liste d&apos;attente
                      </Badge>
                    ) : booking.appointment_request ? (
                      <Badge variant="outline" className="border-indigo-300 text-[#3a00e0]">
                        RDV à rappeler
                      </Badge>
                    ) : booking.is_lead ? (
                      <Badge variant="outline" className="border-amber-300 text-amber-700">
                        Lead panier
                      </Badge>
                    ) : (
                      <span className="text-slate-500 text-sm capitalize">{booking.booking_type || "—"}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-500 text-sm whitespace-nowrap">
                    {formatDate(booking.created_at)}
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    {rowActions(booking)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AdminTable>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className={`${ADMIN_PANEL} text-slate-900 max-w-lg`}>
          <DialogHeader>
            <DialogTitle>Nouvelle réservation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="booking-schedule" className={ADMIN_LABEL}>
                Créneau *
              </Label>
              <select
                id="booking-schedule"
                value={form.schedule_id}
                onChange={(e) => {
                  setForm({ ...form, schedule_id: e.target.value });
                  if (errors.schedule_id) setErrors({});
                }}
                aria-invalid={!!errors.schedule_id}
                aria-describedby={errors.schedule_id ? "booking-schedule-error" : undefined}
                className={`${ADMIN_SELECT} ${errors.schedule_id ? "border-red-500" : ""}`}
              >
                <option value="">— Choisir —</option>
                {schedules
                  .filter((s) => s.active)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {scheduleLabel(s)}
                    </option>
                  ))}
              </select>
              {errors.schedule_id && (
                <p id="booking-schedule-error" className="text-red-600 text-xs" role="alert">
                  {errors.schedule_id}
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="booking-parent" className={ADMIN_LABEL}>
                  Nom parent *
                </Label>
                <Input
                  id="booking-parent"
                  value={form.parent_name}
                  onChange={(e) => {
                    setForm({ ...form, parent_name: e.target.value });
                    if (errors.parent_name) setErrors({});
                  }}
                  aria-invalid={!!errors.parent_name}
                  aria-describedby={errors.parent_name ? "booking-parent-error" : undefined}
                  className={`${ADMIN_FIELD} ${errors.parent_name ? "border-red-500" : ""}`}
                />
                {errors.parent_name && (
                  <p id="booking-parent-error" className="text-red-600 text-xs" role="alert">
                    {errors.parent_name}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="booking-email" className={ADMIN_LABEL}>
                  Email parent
                </Label>
                <Input
                  id="booking-email"
                  type="email"
                  value={form.parent_email}
                  onChange={(e) => setForm({ ...form, parent_email: e.target.value })}
                  className={ADMIN_FIELD}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="booking-phone" className={ADMIN_LABEL}>
                  Téléphone
                </Label>
                <Input
                  id="booking-phone"
                  value={form.parent_phone}
                  onChange={(e) => setForm({ ...form, parent_phone: e.target.value })}
                  className={ADMIN_FIELD}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="booking-child" className={ADMIN_LABEL}>
                  Prénom enfant
                </Label>
                <Input
                  id="booking-child"
                  value={form.child_name}
                  onChange={(e) => setForm({ ...form, child_name: e.target.value })}
                  className={ADMIN_FIELD}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="booking-age" className={ADMIN_LABEL}>
                  Âge enfant
                </Label>
                <Input
                  id="booking-age"
                  type="number"
                  value={form.child_age}
                  onChange={(e) => setForm({ ...form, child_age: e.target.value })}
                  className={ADMIN_FIELD}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="booking-amount" className={ADMIN_LABEL}>
                  Montant (€)
                </Label>
                <Input
                  id="booking-amount"
                  type="number"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className={ADMIN_FIELD}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="booking-status" className={ADMIN_LABEL}>
                Statut
              </Label>
              <select
                id="booking-status"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className={ADMIN_SELECT}
              >
                <option value="confirmed">Confirmée</option>
                <option value="pending">En attente</option>
                <option value="cancelled">Annulée</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)} className={BTN_OUTLINE} disabled={saving}>
              Annuler
            </Button>
            <Button onClick={handleCreate} disabled={saving} className={BTN_PRIMARY}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Création...
                </>
              ) : (
                "Créer"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AdminDetailDialog
        open={!!detail}
        onOpenChange={(open) => !open && setDetail(null)}
        title={detail?.parent_name || "Réservation"}
        footer={
          detail && detail.status !== "cancelled" ? (
            <Button
              variant="ghost"
              className="text-amber-600 hover:text-amber-700 sm:mr-auto"
              onClick={() => {
                setCancelTarget(detail);
                setDetail(null);
              }}
            >
              <Ban className="h-4 w-4 mr-1" />
              Annuler la réservation
            </Button>
          ) : null
        }
      >
        {detail ? (
          <>
            <AdminDetailSection title="Statut">
              <AdminDetailField label="Statut">
                {statusLabels[detail.status] || detail.status}
              </AdminDetailField>
              <AdminDetailField label="Type">{typeLabel(detail)}</AdminDetailField>
              <AdminDetailField label="Créée le">{formatDate(detail.created_at)}</AdminDetailField>
              <AdminDetailField label="Mise à jour">{formatDate(detail.updated_at)}</AdminDetailField>
            </AdminDetailSection>
            <AdminDetailSection title="Parent">
              <AdminDetailField label="Nom">{detail.parent_name}</AdminDetailField>
              <AdminDetailField label="Email">
                {detail.parent_email ? (
                  <a href={`mailto:${detail.parent_email}`} className="text-[#3a00e0] hover:underline">
                    {detail.parent_email}
                  </a>
                ) : null}
              </AdminDetailField>
              <AdminDetailField label="Téléphone">
                {detail.parent_phone ? (
                  <a href={`tel:${detail.parent_phone}`} className="text-[#3a00e0] hover:underline">
                    {detail.parent_phone}
                  </a>
                ) : null}
              </AdminDetailField>
            </AdminDetailSection>
            <AdminDetailSection title="Enfant">
              <AdminDetailField label="Prénom">{detail.child_name}</AdminDetailField>
              <AdminDetailField label="Âge">
                {detail.child_age != null ? `${detail.child_age} ans` : null}
              </AdminDetailField>
            </AdminDetailSection>
            <AdminDetailSection title="Offre & créneau">
              <AdminDetailField label="Offre">
                {detail.schedule_info?.offer_name || detail.schedule_info?.workshop_name}
              </AdminDetailField>
              <AdminDetailField label="Formule">{detail.schedule_info?.formula_name}</AdminDetailField>
              <AdminDetailField label="Centre">{detail.schedule_info?.center_name}</AdminDetailField>
              <AdminDetailField label="Adresse">{detail.schedule_info?.center_address}</AdminDetailField>
              <AdminDetailField label="Date">
                {detail.session_start
                  ? `${detail.session_start}${detail.session_end && detail.session_end !== detail.session_start ? ` → ${detail.session_end}` : ""}`
                  : detail.schedule_info?.start_date
                    ? `${detail.schedule_info.start_date}${detail.schedule_info.end_date && detail.schedule_info.end_date !== detail.schedule_info.start_date ? ` → ${detail.schedule_info.end_date}` : ""}`
                    : null}
              </AdminDetailField>
              <AdminDetailField label="Jour">{detail.schedule_info?.day}</AdminDetailField>
              <AdminDetailField label="Horaire">{detail.schedule_info?.time}</AdminDetailField>
            </AdminDetailSection>
            <AdminDetailSection title="Paiement">
              <AdminDetailField label="Montant dû">{money(detail.amount)}</AdminDetailField>
              <AdminDetailField label="Prix initial">{money(detail.original_amount)}</AdminDetailField>
              <AdminDetailField label="Réduction">
                {detail.discount > 0 ? `-${detail.discount} €` : null}
              </AdminDetailField>
              <AdminDetailField label="Code promo">{detail.promo_code}</AdminDetailField>
              <AdminDetailField label="Mode de paiement">{paymentLabel(detail)}</AdminDetailField>
              <AdminDetailField label="Email confirmation">{detail.email_sent ? "Oui" : "Non"}</AdminDetailField>
              <AdminDetailField label="Créée par admin">{detail.created_by_admin ? "Oui" : "Non"}</AdminDetailField>
              <AdminDetailField label="Session Stripe">{detail.stripe_session_id}</AdminDetailField>
              <AdminDetailField label="Réf. paiement">{detail.payment_intent_id}</AdminDetailField>
            </AdminDetailSection>
            <AdminDetailSection title="Référence" cols={1}>
              <AdminDetailField label="Identifiant">{detail.id}</AdminDetailField>
            </AdminDetailSection>
          </>
        ) : null}
      </AdminDetailDialog>

      <ConfirmDialog
        open={!!cancelTarget}
        onOpenChange={(open) => !open && !cancelling && setCancelTarget(null)}
        title={`Annuler la réservation de ${cancelTarget?.parent_name || ""} ?`}
        description={`La réservation de ${cancelTarget?.parent_name || "ce parent"} passera en statut Annulée. Elle restera dans l'historique.`}
        confirmLabel="Annuler la réservation"
        confirmingLabel="Annulation..."
        onConfirm={confirmCancel}
        confirming={cancelling}
      />
    </div>
  );
}
